import { env } from "~/config/env";
import type { CMSMedia } from "~/types/cms";

/**
 * Simple media object with just id and url (used in shows, hosts, etc.)
 */
export type SimpleMediaObject = {
  id: string;
  url: string;
  alt?: string;
  width?: number;
  height?: number;
};

export interface ResolvedMedia {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}

/**
 * Check if media has a valid finite duration (not a live stream)
 * @param duration - The duration value to check
 * @returns true if duration is finite and valid, false for live streams
 */
export function hasFiniteDuration(duration: number | undefined): boolean {
  return duration !== undefined && isFinite(duration) && duration > 0;
}

/**
 * Format time in seconds to MM:SS format
 * @param seconds - The time in seconds
 * @returns Formatted time string (e.g., "4:15")
 */
export function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const paddedSecs = secs < 10 ? "0" + secs : secs;
  return `${mins}:${paddedSecs}`;
}

/**
 * Resolve media URL to full URL
 * Handles string URLs, CMSMedia objects, and simple media objects
 * If the URL is already absolute (starts with http), return it as-is
 * Otherwise, prepend the CMS API URL for relative paths
 * @param media - The media URL (string), CMSMedia object, simple media object, or number (unpopulated ID), can be undefined/null
 * @returns Full URL or null if no media provided or if unpopulated (number ID)
 */
export function resolveMediaUrl(
  media: string | number | CMSMedia | SimpleMediaObject | undefined | null
): string | null {
  if (!media) return null;

  // Handle number IDs (unpopulated relationships) - cannot resolve to URL
  if (typeof media === "number") return null;

  // Handle string URLs
  if (typeof media === "string") {
    // If URL is already absolute, use it directly
    if (media.startsWith("http")) return media;
    // Otherwise prepend CMS API URL
    return `${env.cmsApiUrl}${media}`;
  }

  // Handle CMSMedia objects
  const url = media.url;
  if (!url) return null;

  // If URL is already absolute, use it directly
  if (url.startsWith("http")) return url;
  // Otherwise prepend CMS API URL
  return `${env.cmsApiUrl}${url}`;
}

/**
 * Resolve the preferred Open Graph image for a media object
 * Prefers the CMS-generated `og` size variant (1200x630, the Open Graph standard)
 * and falls back to the original file with its real dimensions, so emitted
 * og:image:width/height always match the served image
 */
export function resolveOgImage(
  media: string | number | CMSMedia | SimpleMediaObject | undefined | null
): ResolvedMedia | null {
  if (!media || typeof media === "number" || typeof media === "string") {
    return resolveMedia(media);
  }

  const og = "sizes" in media ? media.sizes?.og : undefined;
  if (og?.url) {
    return {
      url: og.url.startsWith("http") ? og.url : `${env.cmsApiUrl}${og.url}`,
      width: og.width ?? undefined,
      height: og.height ?? undefined,
      alt: media.alt || undefined,
    };
  }

  return resolveMedia(media);
}

/**
 * Resolve media object to get URL and dimensions
 * Handles string URLs, CMSMedia objects, simple media objects, and number IDs (unpopulated)
 */
export function resolveMedia(
  media: string | number | CMSMedia | SimpleMediaObject | undefined | null
): ResolvedMedia | null {
  if (!media) return null;

  // Handle number IDs (unpopulated relationships) - cannot resolve
  if (typeof media === "number") return null;

  let url: string | null = null;
  let width: number | undefined;
  let height: number | undefined;
  let alt: string | undefined;

  if (typeof media === "string") {
    url = media;
  } else {
    url = media.url;
    width = media.width;
    height = media.height;
    alt = media.alt || undefined;
  }

  if (!url) return null;

  if (!url.startsWith("http")) {
    url = `${env.cmsApiUrl}${url}`;
  }

  return { url, width, height, alt };
}

/**
 * Read the TCF consent string from Usercentrics internal state.
 * Uses a type assertion since `cmpController` is not part of the public UCCmp API.
 */
function getTcfConsentString(): string {
  if (typeof window === "undefined") return "usercentrics_not_implemented";

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tcString = (window.__ucCmp as any)?.cmpController?.consent?.tcString;
    return typeof tcString === "string" && tcString ? tcString : "null";
  } catch {
    return "usercentrics_not_implemented";
  }
}

/**
 * Append tracking, consent, and cache-busting parameters to a stream URL.
 * Mirrors the parameter set used by the TV app for ad-server compatibility.
 */
export const appendParamsToMediaUrl = (url: string): string => {
  if (!url) return url;

  const parsed = new URL(url);

  // Always refresh the cache-busting timestamp
  parsed.searchParams.set("t", String(Date.now()));

  // Only fill in params not already provided by the CMS
  const defaults: Record<string, string> = {
    aggregator: "website",
    companionAds: "false",
    "aw_0_1st.playerId": "website-player",
    "aw_0_req.userConsentV2": getTcfConsentString(),
  };

  for (const [key, value] of Object.entries(defaults)) {
    if (!parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, value);
    }
  }

  const finalUrl = parsed.toString();
  if (import.meta.env.DEV) {
    console.debug("[Stream URL]", finalUrl);
  }
  return finalUrl;
};
